/**
 * The Items price sheet — pure rules, no React, no Firestore.
 *
 * THE ONE PRINCIPLE (docs/superpowers/specs/2026-09-21-bulk-menu-pricing-design.md §2):
 * a cell shows what the till and the storefront would charge, computed by the
 * shared resolver (`explainItemPrice`), and an edit writes exactly ONE storage
 * location — `Item.menuGroupPrices` (+ `pricingStrategy`, `basePrice`). Never
 * `MenuGroup.items[].priceOverride`. Whatever else decides a price (a group
 * strategy, a parent SubgroupReference override) makes the cell `locked`.
 */
import type { IItem } from './Item';
import type { GroupPricingStrategy, IMenuGroup } from './MenuGroup';
import type { IMenuGroupReference } from './MenuGroupReference';
import { explainItemPrice, type ItemPriceSource } from './menuPricingResolver';
import { enumerateMenuGroupPaths, type ItemMenuPath } from './menuTree';
import type { PricingStrategy } from './types';

export interface SheetColumn {
  readonly path: readonly number[];
  readonly label: string;
}

export type SheetCellInheritedFrom =
  | { readonly kind: 'path'; readonly path: readonly number[] }
  | { readonly kind: 'group_entry'; readonly menuGroupId: number }
  | { readonly kind: 'item_reference'; readonly menuGroupId: number }
  | { readonly kind: 'base' };

export type SheetCell =
  | { readonly state: 'unreachable' }
  | {
      readonly state: 'locked';
      readonly price: number | null;
      readonly by: { readonly menuGroupId: number; readonly strategy: GroupPricingStrategy; readonly viaParentOverride: boolean };
    }
  | { readonly state: 'not_flat'; readonly strategy: PricingStrategy }
  | { readonly state: 'explicit'; readonly price: number }
  | { readonly state: 'inherited'; readonly price: number; readonly from: SheetCellInheritedFrom }
  | { readonly state: 'varies'; readonly min: number; readonly max: number; readonly own: number | null };

export interface ItemPricingFields {
  readonly pricingStrategy: PricingStrategy;
  readonly basePrice: number | undefined;
  readonly menuGroupPrices: readonly IMenuGroupReference[];
}

const EDITABLE: ReadonlySet<PricingStrategy> = new Set<PricingStrategy>(['base', 'menu_specific']);

/** Only Fixed and Menu-specific items carry one price per menu. */
export function isSheetEditable(strategy: PricingStrategy): boolean {
  return EDITABLE.has(strategy);
}

export function samePath(a: readonly number[], b: readonly number[]): boolean {
  return a.length === b.length && a.every((id, i) => id === b[i]);
}

export function isPathPrefix(prefix: readonly number[], path: readonly number[]): boolean {
  return prefix.length <= path.length && prefix.every((id, i) => id === path[i]);
}

export function roundToCents(value: number): number {
  // `(value + EPSILON) * 100` still rounds ~4.6% of half-cent inputs DOWN
  // above ~$2 (2.135 -> 2.13, 4.015 -> 4.01) because float noise can land on
  // either side of *.5. Cleaning the noise via a fixed-decimal string first
  // (4.015 * 100 = 401.49999999999994 -> "401.50000000" -> 402) makes the
  // half-up rounding reliable regardless of which side the noise falls on.
  return Math.round(Number((value * 100).toFixed(8))) / 100;
}

export function validateSheetPrice(value: number): number {
  if (!Number.isFinite(value) || value < 0) {
    throw new RangeError(`A price must be a number of 0 or more (got ${value}).`);
  }
  return roundToCents(value);
}

function plainRef(ref: IMenuGroupReference): IMenuGroupReference {
  return ref.ancestorPath && ref.ancestorPath.length > 0
    ? { menuGroupId: ref.menuGroupId, price: ref.price, ancestorPath: [...ref.ancestorPath] }
    : { menuGroupId: ref.menuGroupId, price: ref.price };
}

export function pricingFieldsOf(
  item: Pick<IItem, 'pricingStrategy' | 'basePrice' | 'menuGroupPrices'>,
): ItemPricingFields {
  return {
    pricingStrategy: item.pricingStrategy ?? 'base',
    basePrice: item.basePrice,
    menuGroupPrices: (item.menuGroupPrices ?? []).map(plainRef),
  };
}

/** The item as it would be with these pricing fields — for re-resolving a draft. */
export function withPricingFields(item: IItem, fields: ItemPricingFields): IItem {
  return { ...item, ...fields, menuGroupPrices: fields.menuGroupPrices } as unknown as IItem;
}

function ownEntry(fields: Pick<ItemPricingFields, 'menuGroupPrices'>, path: readonly number[]): IMenuGroupReference | undefined {
  return fields.menuGroupPrices.find((r) => r.ancestorPath != null && r.ancestorPath.length > 0 && samePath(r.ancestorPath, path));
}

function inheritedFrom(source: ItemPriceSource): SheetCellInheritedFrom {
  switch (source.kind) {
    case 'menu_group_price':
      return source.ancestorPath
        ? { kind: 'path', path: source.ancestorPath }
        : { kind: 'group_entry', menuGroupId: source.menuGroupId };
    case 'item_reference':
      return { kind: 'item_reference', menuGroupId: source.menuGroupId };
    default:
      return { kind: 'base' };
  }
}

export function resolveSheetCell(
  item: IItem,
  column: SheetColumn,
  groups: IMenuGroup[],
  itemPaths: readonly ItemMenuPath[],
): SheetCell {
  const under = itemPaths.filter((p) => isPathPrefix(column.path, p.menuGroupPath));
  if (under.length === 0) return { state: 'unreachable' };

  const explained = under.map((p) => explainItemPrice(item, [...p.menuGroupPath], p.menuGroupId, groups));

  // A group strategy on ANY path under this column decides that path; the
  // sheet cannot write around it, so the whole cell is locked and says why.
  const groupSource = explained.find((e) => e.source.kind === 'group')?.source;
  if (groupSource && groupSource.kind === 'group') {
    const flats = explained.map((e) => (e.price.kind === 'flat' ? e.price.unitPrice : null));
    const unanimous = flats.every((p) => p !== null && p === flats[0]) ? flats[0] : null;
    return {
      state: 'locked',
      price: unanimous,
      by: { menuGroupId: groupSource.menuGroupId, strategy: groupSource.strategy, viaParentOverride: groupSource.viaParentOverride },
    };
  }

  if (!isSheetEditable(item.pricingStrategy)) return { state: 'not_flat', strategy: item.pricingStrategy };

  const prices = explained.map((e) => (e.price.kind === 'flat' ? e.price.unitPrice : 0));
  const min = Math.min(...prices);
  const max = Math.max(...prices);
  const own = item.pricingStrategy === 'menu_specific' ? ownEntry(pricingFieldsOf(item), column.path)?.price ?? null : null;
  if (min !== max) return { state: 'varies', min, max, own };
  if (own !== null && own === min) return { state: 'explicit', price: min };
  return { state: 'inherited', price: min, from: inheritedFrom(explained[0].source) };
}

function assertEditable(fields: ItemPricingFields): void {
  if (!isSheetEditable(fields.pricingStrategy)) {
    throw new RangeError(`A ${fields.pricingStrategy} item has no single price per menu — edit it in the item.`);
  }
}

/**
 * Set (or clear, with `null`) the price at one menu path.
 *
 * A price on a Fixed item makes it Menu-specific; clearing the last price makes
 * it Fixed again — unless it was already Menu-specific with no prices before
 * this session (`original`), which the operator chose and we leave alone.
 */
export function setMenuPrice(
  draft: ItemPricingFields,
  original: ItemPricingFields,
  path: readonly number[],
  price: number | null,
): ItemPricingFields {
  assertEditable(draft);
  if (path.length === 0) throw new RangeError('A menu price needs a menu path.');
  // The item modal's `base` branch never sends menuGroupPrices, so a Fixed
  // draft can still carry old entries the resolver ignores while pricingStrategy
  // is 'base'. Starting the switch from an empty list keeps them from going
  // live silently; diffPricingFields then shows each one dropped (`to: null`).
  const live = draft.pricingStrategy === 'base' ? [] : draft.menuGroupPrices;
  const rest = live.filter((r) => !(r.ancestorPath && r.ancestorPath.length > 0 && samePath(r.ancestorPath, path)));

  if (price === null) {
    const revert =
      draft.pricingStrategy === 'menu_specific' &&
      rest.length === 0 &&
      (original.pricingStrategy !== 'menu_specific' || original.menuGroupPrices.length > 0);
    return { ...draft, menuGroupPrices: rest, pricingStrategy: revert ? 'base' : draft.pricingStrategy };
  }

  const entry: IMenuGroupReference = {
    menuGroupId: path[path.length - 1],
    price: validateSheetPrice(price),
    ancestorPath: [...path],
  };
  return { ...draft, pricingStrategy: 'menu_specific', menuGroupPrices: [...rest, entry] };
}

export function setPricingStrategy(draft: ItemPricingFields, strategy: 'base' | 'menu_specific'): ItemPricingFields {
  assertEditable(draft);
  if (strategy === 'base') {
    // Menu prices under a Fixed item are inert data the resolver ignores — drop them.
    return { ...draft, pricingStrategy: 'base', menuGroupPrices: [] };
  }
  // Switching FROM base must not resurrect dormant entries — start clean,
  // exactly like setMenuPrice's non-null branch.
  return {
    ...draft,
    pricingStrategy: 'menu_specific',
    menuGroupPrices: draft.pricingStrategy === 'base' ? [] : draft.menuGroupPrices,
  };
}

export function setBasePrice(draft: ItemPricingFields, price: number): ItemPricingFields {
  assertEditable(draft);
  return { ...draft, basePrice: validateSheetPrice(price) };
}

export type FillRule =
  | { readonly kind: 'set'; readonly amount: number }
  | { readonly kind: 'percent_of_base'; readonly percent: number }
  | { readonly kind: 'base_minus'; readonly amount: number }
  | { readonly kind: 'base_plus'; readonly amount: number }
  | { readonly kind: 'clear' };

/**
 * The value a fill writes for one row; `null` = clear. Throws RangeError on a
 * price below zero, and on a base-relative rule (`percent_of_base`,
 * `base_minus`, `base_plus`) when the item has no base price to be relative
 * to — treating an absent base as 0 would silently write a $0.00-derived
 * price. `set` and `clear` don't need a base and are unaffected.
 */
export function fillValue(rule: FillRule, basePrice: number | undefined): number | null {
  if (rule.kind === 'clear') return null;
  if (rule.kind === 'set') return validateSheetPrice(rule.amount);
  if (basePrice === undefined) throw new RangeError('This item has no base price.');
  switch (rule.kind) {
    case 'percent_of_base':
      return validateSheetPrice((basePrice * rule.percent) / 100);
    case 'base_minus':
      return validateSheetPrice(basePrice - rule.amount);
    case 'base_plus':
      return validateSheetPrice(basePrice + rule.amount);
  }
}

const entryKey = (r: IMenuGroupReference): string =>
  r.ancestorPath && r.ancestorPath.length > 0 ? r.ancestorPath.join('>') : `group:${r.menuGroupId}`;

export type PriceChange =
  | { readonly kind: 'strategy'; readonly from: PricingStrategy; readonly to: PricingStrategy }
  | { readonly kind: 'base'; readonly from: number | undefined; readonly to: number | undefined }
  | {
      readonly kind: 'menu_price';
      readonly key: string;
      readonly path: readonly number[];
      readonly legacy: boolean;
      readonly from: number | null;
      readonly to: number | null;
    };

/**
 * `key -> the FIRST entry stored under it, and how many entries share the key`.
 * The resolver (`resolveMenuSpecificPrice`) and `ownEntry` both take the first
 * matching entry when duplicates exist, so the diff must read storage the
 * same way — a last-wins map would report an edit that removes a stale
 * duplicate as a no-op whenever the surviving price happens to be the one
 * last-wins already saw.
 */
function firstEntryByKey(refs: readonly IMenuGroupReference[]): Map<string, { ref: IMenuGroupReference; count: number }> {
  const map = new Map<string, { ref: IMenuGroupReference; count: number }>();
  for (const r of refs) {
    const key = entryKey(r);
    const existing = map.get(key);
    map.set(key, existing ? { ref: existing.ref, count: existing.count + 1 } : { ref: r, count: 1 });
  }
  return map;
}

export function diffPricingFields(original: ItemPricingFields, draft: ItemPricingFields): PriceChange[] {
  const changes: PriceChange[] = [];
  if (original.pricingStrategy !== draft.pricingStrategy) {
    changes.push({ kind: 'strategy', from: original.pricingStrategy, to: draft.pricingStrategy });
  }
  if (original.basePrice !== draft.basePrice) {
    changes.push({ kind: 'base', from: original.basePrice, to: draft.basePrice });
  }
  const before = firstEntryByKey(original.menuGroupPrices);
  const after = firstEntryByKey(draft.menuGroupPrices);
  for (const key of new Set([...before.keys(), ...after.keys()])) {
    const a = before.get(key);
    const b = after.get(key);
    // A dropped/added duplicate is a real storage change even when the
    // first-wins price is unchanged on both sides.
    if (a?.ref.price === b?.ref.price && (a?.count ?? 0) === (b?.count ?? 0)) continue;
    const ref = (b?.ref ?? a?.ref) as IMenuGroupReference;
    const legacy = !(ref.ancestorPath && ref.ancestorPath.length > 0);
    changes.push({
      kind: 'menu_price',
      key,
      path: legacy ? [ref.menuGroupId] : [...(ref.ancestorPath as number[])],
      legacy,
      from: a?.ref.price ?? null,
      to: b?.ref.price ?? null,
    });
  }
  return changes;
}

export function samePricingFields(a: ItemPricingFields, b: ItemPricingFields): boolean {
  return diffPricingFields(a, b).length === 0;
}

export interface ItemPricePatch {
  readonly itemId: number;
  /** The fields as read when the sheet opened — the write is refused if the stored ones differ. */
  readonly expected: ItemPricingFields;
  readonly next: ItemPricingFields;
}

export function buildItemPricePatch(
  itemId: number,
  original: ItemPricingFields,
  draft: ItemPricingFields,
): ItemPricePatch | null {
  return samePricingFields(original, draft) ? null : { itemId, expected: original, next: draft };
}

/** Would the till charge `typed` in this column once the draft is saved? */
export function isEditEffective(
  item: IItem,
  draft: ItemPricingFields,
  column: SheetColumn,
  typed: number,
  groups: IMenuGroup[],
  itemPaths: readonly ItemMenuPath[],
): boolean {
  const after = resolveSheetCell(withPricingFields(item, draft), column, groups, itemPaths);
  return after.state === 'explicit' && after.price === roundToCents(typed);
}

const PATH_SEPARATOR = ' › ';

/** Every root, plus every path an item already carries a price at — in walk order. */
export function defaultSheetColumns(
  groups: IMenuGroup[],
  items: readonly Pick<IItem, 'menuGroupPrices' | 'pricingStrategy'>[],
): SheetColumn[] {
  const wanted = new Set<string>();
  for (const item of items) {
    // Entries on a non-menu_specific item are dormant — the resolver never
    // reads them, so they must not conjure a column into existence.
    if (item.pricingStrategy !== 'menu_specific') continue;
    for (const r of item.menuGroupPrices ?? []) {
      if (r.ancestorPath && r.ancestorPath.length > 0) wanted.add(r.ancestorPath.join('>'));
    }
  }
  const seen = new Set<string>();
  const columns: SheetColumn[] = [];
  for (const p of enumerateMenuGroupPaths(groups)) {
    const key = p.path.join('>');
    if (seen.has(key)) continue;
    if (p.path.length === 1 || wanted.has(key)) {
      seen.add(key);
      columns.push({ path: p.path, label: p.names.join(PATH_SEPARATOR) });
    }
  }
  return columns;
}

export interface ItemPricingSummary {
  readonly label: string;
  readonly detail: string | null;
  readonly sortPrice: number;
}

const money = (n: number): string => `$${n.toFixed(2)}`;
const range = (prices: number[]): string | null => {
  const real = prices.filter((p) => typeof p === 'number' && p > 0);
  if (real.length === 0) return null;
  const lo = Math.min(...real);
  const hi = Math.max(...real);
  return lo === hi ? money(lo) : `${money(lo)} – ${money(hi)}`;
};

/** The Items list's Pricing column: label, detail line, and the number it sorts on. */
export function describeItemPricing(item: IItem): ItemPricingSummary {
  const base = item.basePrice ?? 0;
  switch (item.pricingStrategy) {
    case 'base':
      return { label: 'Fixed', detail: item.basePrice ? money(item.basePrice) : null, sortPrice: base };
    case 'menu_specific': {
      const prices = [base, ...(item.menuGroupPrices ?? []).map((r) => r.price)];
      const real = prices.filter((p) => p > 0);
      return { label: 'Menu', detail: range(prices), sortPrice: real.length ? Math.min(...real) : base };
    }
    case 'size': {
      const prices = (item.sizePrice ?? []).map((s) => s.price);
      const real = prices.filter((p) => p > 0);
      return { label: 'Size-based', detail: range(prices), sortPrice: real.length ? Math.min(...real) : base };
    }
    case 'variant': {
      const prices = (item.variants ?? []).map((v) => v.price);
      const real = prices.filter((p) => p > 0);
      const n = item.variants?.length ?? 0;
      return { label: n > 0 ? `Variants (${n})` : 'Variants', detail: range(prices), sortPrice: real.length ? Math.min(...real) : base };
    }
    case 'open':
      return { label: 'Open', detail: 'Open Price', sortPrice: base };
    default:
      return { label: 'Open', detail: null, sortPrice: base };
  }
}
